import FileUploadIcon from "@mui/icons-material/FileUpload";
import { LoadingButton } from "@mui/lab";
import { Button, Select, Switch } from "@mui/material";
import Input from "@mui/material/Input";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { DataGrid, GridColumns } from "@mui/x-data-grid";
import { useApiUrl, useUpdate } from "@refinedev/core";
import { List, useDataGrid } from "@refinedev/mui";
import DefaultIceCluster from "@warriortrading/warriortrading.hermes.common.feathers/enums/defaultIceCluster";
import { config } from "config";
import copy from "copy-to-clipboard";
import React, { useCallback, useEffect, useState } from "react";
import { dataProvider } from "rest-data-provider";

const CurrentResource = "settings";

const excludeSettingsNames = [
  "fallbackMenuSwitch",
  "betaMenuSwitch"
];

export const SettingsList = () => {
  const chironQmApiHostOptions = config().chironQmApiHostOptions;
  const { dataGridProps } = useDataGrid();
  const { mutate } = useUpdate();
  const apiUrl = useApiUrl();
  const roomData = useCallback(() => {
    return dataProvider(apiUrl)
      .getList({ resource: "rooms" })
      .then((res) => {
        setRoomArr(res?.data);
        return res?.data;
      });
  }, [apiUrl]);

  const roomMemberData = useCallback(() => {
    return dataProvider(apiUrl)
      .getList({
        resource: "room-member",
        filters: [
          {
            field: "role[lte]",
            operator: "eq",
            value: 1,
          },
        ],
      })
      .then((res) => {
        const roomMembersMap = new Map();
        const roomMembersSource = res?.data;
        if (roomMembersSource && roomMembersSource.length !== 0) {
          roomMembersSource.forEach((source: any) =>
            roomMembersMap.set(
              source.userId,
              `${source["user.nickname"]}(${source["user.email"]})`
            )
          );
          const moderatorUserList = Array.from(roomMembersMap)
            .map((members) => ({ value: members[0], name: members[1] }))
            .sort(function (a, b) {
              const nameA = a.name.toUpperCase();
              const nameB = b.name.toUpperCase();
              if (nameA < nameB) {
                return -1;
              }
              if (nameA > nameB) {
                return 1;
              }
              return 0;
            });
          setRoomMemberArr(moderatorUserList);
        }
        return res?.data;
      });
  }, [apiUrl]);
  useEffect(() => {
    roomData();
    roomMemberData();
  }, [roomData, roomMemberData]);

  const [roomArr, setRoomArr] = useState<any[]>([]);
  const [roomMemberArr, setRoomMemberArr] = useState<any[]>([]);

  const [dataStatus, setDataStatus] = useState<Record<string, boolean>>({
    sessionLifeTime: true,
    sessionExpandedGap: true,
    reportCounts: true,
    mutingTime: true,
    adminMenus: true,
    messageRekognitionOpt: true,
    maxConcurrentRoomsCount: true,
    wtIceServers: true,
    defaultIceCluster: true,
    wtPercentageInMixedIceCluster: true,
    loadTestOptions: true,
    troubleshootingLink: true,
    supportRoomId: true,
    possiblePlatforms: true,
    possibleOperationSystems: true,
    possibleBrowsers: true,
    announcementKeepTime: true,
    usersUnitForDelay: true,
    baseSecondsForDelay: true,
    maxSecondsForDelay: true,
    ticketEmailReceiver: true,
    ticketEmailSender: true,
    emailWhiteList: true,
    emailRegExp: true,
    scannerAlertSound: true,
    scannerRunningUpSound: true,
    scannerTopListGapperSortAble: true,
    scannerTopListGainerSortAble: true,
    scannerServer: true,
    chironQmApiHost: true,
    bypassAgreement: true,
    scannerHaltSound: true,
    betaRoomNames: true,
    userMaskWhiteList: true,
    defaultChartTemplate: true,
    defaultChartLayout: true,
    analyticsWatchList: true,
    // fallbackMenuSwitch: true,
    // betaMenuSwitch: true,
    simBetaSwitch: true,
    QAScannerServerMenuSwitch: true,
    onboardingVideoUrl: true,
  });

  const [value, setValue] = useState<Record<string, any>>({
    sessionLifeTime: "",
    sessionExpandedGap: "",
    reportCounts: "",
    mutingTime: "",
    adminMenus: "",
    messageRekognitionOpt: "",
    maxConcurrentRoomsCount: "",
    wtIceServers: "",
    defaultIceCluster: "",
    wtPercentageInMixedIceCluster: "",
    loadTestOptions: "",
    troubleshootingLink: {},
    supportRoomId: "",
    possiblePlatforms: "",
    possibleOperationSystems: "",
    possibleBrowsers: "",
    announcementKeepTime: "",
    usersUnitForDelay: "",
    baseSecondsForDelay: "",
    maxSecondsForDelay: "",
    ticketEmailReceiver: "",
    ticketEmailSender: "",
    emailWhiteList: "",
    emailRegExp: "",
    scannerAlertSound: "",
    scannerRunningUpSound: "",
    scannerTopListGapperSortAble: "",
    scannerTopListGainerSortAble: "",
    scannerServer: "",
    chironQmApiHost: "",
    bypassAgreement: "",
    scannerHaltSound: "",
    betaRoomNames: "",
    userMaskWhiteList: [],
    defaultChartTemplate: "",
    defaultChartLayout: "",
    analyticsWatchList: "",
    // fallbackMenuSwitch: "",
    // betaMenuSwitch: "",
    simBetaSwitch: "",
    QAScannerServerMenuSwitch: "",
    onboardingVideoUrl: "",
  });

  useEffect(() => {
    if (dataGridProps?.rows.length !== 0) {
      setValue({
        sessionLifeTime: dataGridProps?.rows.find(
          (item) => item.name === "sessionLifeTime"
        )?.value,
        sessionExpandedGap: dataGridProps?.rows.find(
          (item) => item.name === "sessionExpandedGap"
        )?.value,
        reportCounts: dataGridProps?.rows.find(
          (item) => item.name === "reportCounts"
        )?.value,
        mutingTime: dataGridProps?.rows.find(
          (item) => item.name === "mutingTime"
        )?.value,
        adminMenus: dataGridProps?.rows.find(
          (item) => item.name === "adminMenus"
        )?.value,
        messageRekognitionOpt: dataGridProps?.rows.find(
          (item) => item.name === "messageRekognitionOpt"
        )?.value,
        maxConcurrentRoomsCount: dataGridProps?.rows.find(
          (item) => item.name === "maxConcurrentRoomsCount"
        )?.value,
        wtIceServers: dataGridProps?.rows.find(
          (item) => item.name === "wtIceServers"
        )?.value,
        defaultIceCluster: dataGridProps?.rows.find(
          (item) => item.name === "defaultIceCluster"
        )?.value,
        wtPercentageInMixedIceCluster: dataGridProps?.rows.find(
          (item) => item.name === "wtPercentageInMixedIceCluster"
        )?.value,
        loadTestOptions: dataGridProps?.rows.find(
          (item) => item.name === "loadTestOptions"
        )?.value,
        troubleshootingLink: dataGridProps?.rows.find(
          (item) => item.name === "troubleshootingLink"
        ),
        supportRoomId: dataGridProps?.rows.find(
          (item) => item.name === "supportRoomId"
        )?.value,
        possiblePlatforms: dataGridProps?.rows.find(
          (item) => item.name === "possiblePlatforms"
        )?.value,
        possibleOperationSystems: dataGridProps?.rows.find(
          (item) => item.name === "possibleOperationSystems"
        )?.value,
        possibleBrowsers: dataGridProps?.rows.find(
          (item) => item.name === "possibleBrowsers"
        )?.value,
        announcementKeepTime: dataGridProps?.rows.find(
          (item) => item.name === "announcementKeepTime"
        )?.value,
        usersUnitForDelay: dataGridProps?.rows.find(
          (item) => item.name === "usersUnitForDelay"
        )?.value,
        baseSecondsForDelay: dataGridProps?.rows.find(
          (item) => item.name === "baseSecondsForDelay"
        )?.value,
        maxSecondsForDelay: dataGridProps?.rows.find(
          (item) => item.name === "maxSecondsForDelay"
        )?.value,
        ticketEmailReceiver: dataGridProps?.rows.find(
          (item) => item.name === "ticketEmailReceiver"
        )?.value,
        ticketEmailSender: dataGridProps?.rows.find(
          (item) => item.name === "ticketEmailSender"
        )?.value,
        emailWhiteList: dataGridProps?.rows.find(
          (item) => item.name === "emailWhiteList"
        )?.value,
        emailRegExp: dataGridProps?.rows.find(
          (item) => item.name === "emailRegExp"
        )?.value,
        scannerAlertSound: dataGridProps?.rows.find(
          (item) => item.name === "scannerAlertSound"
        )?.value,
        scannerRunningUpSound: dataGridProps?.rows.find(
          (item) => item.name === "scannerRunningUpSound"
        )?.value,
        scannerTopListGapperSortAble: dataGridProps?.rows.find(
          (item) => item.name === "scannerTopListGapperSortAble"
        )?.value,
        scannerTopListGainerSortAble: dataGridProps?.rows.find(
          (item) => item.name === "scannerTopListGainerSortAble"
        )?.value,
        scannerServer:
          dataGridProps?.rows.find((i) => i.name === "scannerServer")?.value ||
          config().server.stock,
        chironQmApiHost:
          dataGridProps?.rows.find((i) => i.name === "chironQmApiHost")
            ?.value || chironQmApiHostOptions[0],
        bypassAgreement: dataGridProps?.rows.find(
          (item) => item.name === "bypassAgreement"
        )?.value,
        scannerHaltSound: dataGridProps?.rows.find(
          (item) => item.name === "scannerHaltSound"
        )?.value,
        betaRoomNames: dataGridProps?.rows.find(
          (item) => item.name === "betaRoomNames"
        )?.value,
        userMaskWhiteList: JSON.parse(
          dataGridProps?.rows.find((item) => item.name === "userMaskWhiteList")
            ?.value
        ),
        defaultChartTemplate: dataGridProps?.rows.find(
          (item) => item.name === "defaultChartTemplate"
        )?.value,
        defaultChartLayout: dataGridProps?.rows.find(
          (item) => item.name === "defaultChartLayout"
        )?.value,
        analyticsWatchList: dataGridProps?.rows.find(
          (item) => item.name === "analyticsWatchList"
        )?.value,
        // fallbackMenuSwitch: dataGridProps?.rows.find(
        //   (i) => i.name === "fallbackMenuSwitch"
        // )?.value,
        // betaMenuSwitch: dataGridProps?.rows.find(
        //   (i) => i.name === "betaMenuSwitch"
        // )?.value,
        simBetaSwitch: dataGridProps?.rows.find(
          (i) => i.name === "simBetaSwitch"
        )?.value,
        QAScannerServerMenuSwitch: dataGridProps?.rows.find(
          (i) => i.name === "QAScannerServerMenuSwitch"
        )?.value,
        onboardingVideoUrl: dataGridProps?.rows.find(
          (item) => item.name === "onboardingVideoUrl"
        )?.value,
      });
    }
  }, [dataGridProps.rows, chironQmApiHostOptions]);

  const buttonHandler = useCallback(
    (name: string) => {
      setDataStatus({
        ...dataStatus,
        [name]: false,
      });
    },
    [dataStatus]
  );

  const copyFun = (value: string) => {
    copy(value);
  };

  const saveHandler = useCallback(
    (name: string, id: string) => {
      if (name === "sessionLifeTime") {
        const sessionExpandedGapValue = parseInt(
          dataGridProps?.rows.filter(
            (item) => item.name === "sessionExpandedGap"
          )[0]?.value
        );
        if (!(Number(value[name]) - sessionExpandedGapValue >= 60)) {
          window.alert(
            "SessionLifeTime minus sessionExpandGap must be greater than or equal to 60, Please change it again."
          );
          return;
        }
      }
      if (name === "sessionExpandedGap") {
        const sessionLifeTimeValue = parseInt(
          dataGridProps?.rows.filter(
            (item) => item.name === "sessionLifeTime"
          )[0]?.value
        );
        if (
          !(
            Number(value[name]) >= 60 &&
            sessionLifeTimeValue - Number(value[name]) >= 60
          )
        ) {
          window.alert(
            "SessionExpandGap must be greater than or equal to 60 and sessionLifeTime minus sessionExpandGap must be greater than or equal to 60, Please change it again."
          );
          return;
        }
      }
      if (
        name === "messageRekognitionOpt" ||
        name === "wtIceServers" ||
        name === "loadTestOptions" ||
        name === "ticketEmailSender" ||
        name === "scannerTopListGapperSortAble" ||
        name === "scannerTopListGainerSortAble" ||
        name === "analyticsWatchList"
      ) {
        try {
          JSON.parse(value[name]);
        } catch (err) {
          window.alert(
            "The new value is not a valid json string, please check and pay attention to use double quotes!"
          );
          return;
        }
      }
      if (name === "wtIceServers") {
        if (!(value[name].startsWith("[") && value[name].endsWith("]"))) {
          window.alert(
            "Input must begin with a character [ and end with a character ]"
          );
          return;
        }
      }
      if (name === "wtPercentageInMixedIceCluster") {
        if (!(parseInt(value[name]) > 0 && parseInt(value[name]) < 100)) {
          window.alert("Input must between 0 and 100");
          return;
        }
      }
      if (name === "troubleshootingLink") {
        mutate({
          resource: CurrentResource,
          values: {
            dtdValue: value[name].dtdValue,
            value: value[name].value,
            wtValue: value[name].wtValue,
          },
          id,
        });
        setDataStatus({
          ...dataStatus,
          [name]: true,
        });
        // window.history.go();
        return;
      }
      if (name === "userMaskWhiteList") {
        mutate({
          resource: CurrentResource,
          values: {
            value: JSON.stringify(value[name]),
          },
          id,
        });
        setDataStatus({
          ...dataStatus,
          [name]: true,
        });
        return;
      }
      if (
        name === "sessionLifeTime" ||
        name === "sessionExpandedGap" ||
        name === "emailWhiteList" ||
        name === "emailRegExp"
      ) {
        mutate({
          resource: CurrentResource,
          values: {
            value: value[name],
            name,
          },
          id,
        });
        setDataStatus({
          ...dataStatus,
          [name]: true,
        });
        return;
      }
      if (
        name === "scannerAlertSound" ||
        name === "scannerHaltSound" ||
        name === "scannerRunningUpSound"
      ) {
        mutate({
          resource: CurrentResource,
          values: {
            value: value[name],
            name,
          },
          id,
        });
        setDataStatus({
          ...dataStatus,
          [name]: true,
        });
        return;
      }
      mutate({
        resource: CurrentResource,
        values: {
          value: value[name],
          currentServer: dataGridProps?.rows.find(
            (i) => i.name === "scannerServer"
          )?.value,
        },
        id,
      });
      setDataStatus({
        ...dataStatus,
        [name]: true,
      });
    },
    [mutate, value, dataGridProps?.rows, dataStatus]
  );

  // scannerSound part
  const [loadingScannerAlert, setLoadingScannerAlert] = useState(false);
  const [loadingScannerHalt, setLoadingScannerHalt] = useState(false);
  const [loadingScannerRunningUp, setLoadingScannerRunningUp] = useState(false);
  const onAlertChangeHandler = useCallback(
    async (target: any, name: any, setLoadingScanner: any) => {
      try {
        setLoadingScanner(true);
        const formData = new FormData();
        const file: File = (target.files as FileList)[0];
        if (!file?.type?.startsWith("audio")) {
          window.alert(
            "Upload failed! The upload type needs to be an audio type!"
          );
          setLoadingScanner(false);
          return;
        } else if (file?.size > 512000) {
          window.alert("Upload failed! The upload size cannot exceed 500kb!");
          setLoadingScanner(false);
          return;
        }
        formData.append("uri", file);
        const res = await dataProvider(apiUrl)
          .create({ resource: "file", variables: formData })
          .catch(() => {
            window.alert("Upload failed!");
          });
        setValue({
          ...value,
          [name]: res?.data?.rawUploadInfo?.uri,
        });
        setLoadingScanner(false);
      } catch (error) {
        setLoadingScanner(false);
      }
    },
    [apiUrl, value]
  );

  const columns = React.useMemo<GridColumns<any>>(
    () => [
      {
        field: "name",
        headerName: "",
        minWidth: 500,
        flex: 1,
        renderCell: function render({ row }) {
          return (
            <div
              style={{
                marginTop: "10px",
                marginBottom: "10px",
                paddingLeft: "10px",
              }}
            >
              <h3>{row.name}:</h3>
              <div>{row.description}</div>
            </div>
          );
        },
      },
      {
        field: "value",
        headerName: "",
        minWidth: 500,
        flex: 1,
        renderCell: function render({ row }) {
          if (row.name === "defaultIceCluster") {
            return (
              <Select
                fullWidth
                value={value[row.name]}
                onChange={(e) =>
                  setValue({
                    ...value,
                    [row.name]: e.target.value,
                  })
                }
                disabled={dataStatus[row.name as string]}
              >
                {Object.values(DefaultIceCluster)[0].map((item: any) => {
                  return (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          }
          if (row.name === "troubleshootingLink") {
            return (
              <div style={{ width: "500px" }}>
                <div style={{ display: "flex" }}>
                  <InputLabel
                    id="wt-label"
                    style={{
                      width: "50px",
                      marginTop: "10px",
                      marginRight: "10px",
                    }}
                  >
                    WT :{" "}
                  </InputLabel>
                  <Input
                    defaultValue={row.wtValue}
                    inputProps={{ "aria-label": "description" }}
                    disabled={dataStatus[row.name as string]}
                    fullWidth
                    id="audioUpload"
                    onChange={(e) => {
                      setValue({
                        ...value,
                        [row.name]: {
                          ...value[row.name],
                          wtValue: e.target.value,
                        },
                      });
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => copyFun(row.wtValue as string)}
                    style={{ marginLeft: "10px" }}
                  >
                    COPY
                  </Button>
                </div>
                <div style={{ display: "flex", marginTop: "10px" }}>
                  <InputLabel
                    id="dtd-label"
                    style={{
                      width: "60px",
                      marginTop: "10px",
                      marginRight: "10px",
                    }}
                  >
                    DTD :{" "}
                  </InputLabel>
                  <Input
                    defaultValue={row.dtdValue}
                    inputProps={{ "aria-label": "description" }}
                    disabled={dataStatus[row.name as string]}
                    fullWidth
                    onChange={(e) => {
                      setValue({
                        ...value,
                        [row.name]: {
                          ...value[row.name],
                          dtdValue: e.target.value,
                        },
                      });
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => copyFun(row.dtdValue as string)}
                    style={{ marginLeft: "10px" }}
                  >
                    COPY
                  </Button>
                </div>
              </div>
            );
          }
          if (row.name === "supportRoomId") {
            return (
              <Select
                fullWidth
                value={value[row.name]}
                onChange={(e) =>
                  setValue({
                    ...value,
                    [row.name]: e.target.value,
                  })
                }
                disabled={dataStatus[row.name as string]}
              >
                {roomArr?.map((item) => {
                  return (
                    <MenuItem key={item.id} value={item.id}>
                      {item.title}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          }
          if (row.name === "userMaskWhiteList") {
            return (
              <Select
                fullWidth
                value={value[row.name]}
                onChange={(e) =>
                  setValue({
                    ...value,
                    [row.name]: e.target.value,
                  })
                }
                multiple
                disabled={dataStatus[row.name as string]}
              >
                {roomMemberArr?.map((item) => {
                  return (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          }
          if (row.name === "scannerServer") {
            return (
              <Select
                fullWidth
                value={value[row.name]}
                onChange={(e) =>
                  setValue({
                    ...value,
                    [row.name]: e.target.value,
                  })
                }
                disabled={dataStatus[row.name as string]}
              >
                {config().scannerServers.map((item) => {
                  return (
                    <MenuItem key={item.value} value={item.value}>
                      {item.name}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          }
          if (row.name === "chironQmApiHost") {
            return (
              <Select
                fullWidth
                value={value[row.name]}
                onChange={(e) =>
                  setValue({
                    ...value,
                    [row.name]: e.target.value,
                  })
                }
                disabled={dataStatus[row.name as string]}
              >
                {chironQmApiHostOptions.map((item) => {
                  return (
                    <MenuItem key={item} value={item}>
                      {item}
                    </MenuItem>
                  );
                })}
              </Select>
            );
          }
          if (
            row.name === "scannerAlertSound" ||
            row.name === "scannerHaltSound" ||
            row.name === "scannerRunningUpSound"
          ) {
            return (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: "10px",
                  marginBottom: "10px",
                }}
              >
                <div style={{ width: "463px", wordWrap: "break-word" }}>
                  Audio File:{" "}
                  <a href={row.value} target="_blank" rel="noopener noreferrer">
                    {row.value}
                  </a>
                  <audio id={row.id} src={row.value} preload="preload"></audio>
                </div>
                <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                  <Button
                    variant="contained"
                    size="medium"
                    onClick={() => {
                      let audio: HTMLMediaElement = document?.getElementById(
                        row.id
                      ) as HTMLMediaElement;
                      audio?.play();
                    }}
                  >
                    PLAY
                  </Button>
                </div>
                <div>
                  <label htmlFor={`audio${row.name}`}>
                    <Input
                      id={`audio${row.name}`}
                      type="file"
                      sx={{ display: "none" }}
                      // accept="audio/*"
                      onChange={(event) =>
                        onAlertChangeHandler(
                          event.target,
                          row.name,
                          row.name === "scannerAlertSound"
                            ? setLoadingScannerAlert
                            : row.name === "scannerHaltSound"
                            ? setLoadingScannerHalt
                            : setLoadingScannerRunningUp
                        )
                      }
                    />
                    <LoadingButton
                      loading={
                        row.name === "scannerAlertSound"
                          ? loadingScannerAlert
                          : row.name === "scannerHaltSound"
                          ? loadingScannerHalt
                          : loadingScannerRunningUp
                      }
                      loadingPosition="end"
                      endIcon={<FileUploadIcon />}
                      variant="contained"
                      component="span"
                      disabled={dataStatus[row.name as string]}
                    >
                      Upload Audio
                    </LoadingButton>
                  </label>
                </div>
              </div>
            );
          }
          if (
            // row.name === "fallbackMenuSwitch" ||
            // row.name === "betaMenuSwitch" ||
            row.name === "simBetaSwitch" ||
            row.name === "QAScannerServerMenuSwitch"
          ) {
            return (
              <Switch
                checked={value[row.name] === "true"}
                onChange={(e) => {
                  setValue({
                    ...value,
                    [row.name]: e.target.checked ? "true" : "false",
                  });
                }}
                disabled={dataStatus[row.name as string]}
              />
            );
          }
          return (
            <Input
              defaultValue={row.value}
              inputProps={{ "aria-label": "description" }}
              disabled={dataStatus[row.name as string]}
              style={{ marginLeft: "10px", marginRight: "10px" }}
              fullWidth
              onChange={(e) => {
                setValue({
                  ...value,
                  [row.name]: e.target.value,
                });
              }}
            />
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        align: "center",
        headerAlign: "center",
        minWidth: 150,
        renderCell: function render({ row }) {
          if (
            (row.name === "scannerServer" ||
              row.name === "chironQmApiHost" ||
              // row.name === "fallbackMenuSwitch" ||
              // row.name === "betaMenuSwitch" ||
              row.name === "simBetaSwitch" ||
              row.name === "QAScannerServerMenuSwitch") &&
            !dataStatus[row.name]
          ) {
            return (
              <div style={{ display: "flex", gap: "8px" }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    saveHandler(row.name as string, row.id as string)
                  }
                >
                  SAVE
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setValue({
                      ...value,
                      [row.name]:
                        row.name === "scannerServer"
                          ? dataGridProps?.rows.find(
                              (item) => item.name === row.name
                            )?.value || config().server.stock
                          : row.name === "chironQmApiHost"
                          ? dataGridProps?.rows.find(
                              (item) => item.name === row.name
                            )?.value || chironQmApiHostOptions[0]
                          : dataGridProps?.rows.find(
                              (item) => item.name === row.name
                            )?.value,
                    });
                    setDataStatus({
                      ...dataStatus,
                      [row.name]: true,
                    });
                  }}
                >
                  CANCEL
                </Button>
              </div>
            );
          }

          return (
            <>
              {dataStatus[row.name] ? (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => buttonHandler(row.name as string)}
                >
                  EDIT
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() =>
                    saveHandler(row.name as string, row.id as string)
                  }
                >
                  SAVE
                </Button>
              )}
            </>
          );
        },
      },
    ],
    [
      dataStatus,
      value,
      roomArr,
      roomMemberArr,
      loadingScannerAlert,
      loadingScannerHalt,
      loadingScannerRunningUp,
      onAlertChangeHandler,
      saveHandler,
      dataGridProps?.rows,
      buttonHandler,
      chironQmApiHostOptions,
    ]
  );

  return (
    <List>
      <DataGrid
        {...dataGridProps}
        rows={(dataGridProps.rows ?? []).filter(
          (row) => !excludeSettingsNames.includes(row.name)
        )}
        columns={columns}
        autoHeight
        hideFooterPagination
        getRowHeight={() => "auto"}
      />
    </List>
  );
};
